import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { makeGetTask, makeGetProjectTasks } from 'selectors/tasks';
import { makeGetSortedActiveProjectTaskGroups, makeGetSortedActiveProjectTaskGroupsWithTasks } from 'selectors/task-groups';

import { updateTask, addTaskLabels } from 'actions/tasks';

import { canUser } from 'services/permissions-service';
import { trackEvent } from 'services/events-tracker-service';

import DropdownBtn from 'components/dropdown-btn';
import Popout from 'components/popout';
import Select from 'components/select';

import IconEdit from 'icons/icon-edit';

const EMPTY_ARRAY = [];

const UTIL_ACTION_ITEMS = [
    {
        text: 'Delete',
        action: 'delete'
    },
    {
        text: 'Convert to a task',
        action: 'convertToTask'
    },
    {
        text: 'Move into another task',
        action: 'moveToTask'
    }
];

const NON_REMOVE_UTIL_ACTION_ITEMS = [
    {
        text: 'Convert to a task',
        action: 'convertToTask'
    },
    {
        text: 'Move into another task',
        action: 'moveToTask'
    }
];

const SubtaskUtils = ({ id, subtask, parentTaskLabels, tasks, taskGroups, taskGroupsWithTasks, btnClassName = 'btn btn-square btn-xxxsmall btn-simple btn-icon item-click-action', icon = <IconEdit size="10px" />, onDelete }) => {
    const convertToTaskPopoutCtrl = useRef(null);
    const moveToTaskPopoutCtrl = useRef(null);
    const convertToTaskSelectCtrl = useRef(null);
    const moveToTaskTaskListSelectCtrl = useRef(null);
    const moveToTaskTaskSelectCtrl = useRef(null);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [moveToTaskSelectedTaskList, setMoveToTaskSelectedTaskList] = useState(taskGroupsWithTasks[0]?.id);

    const createdBy = useMemo(() => {
        return subtask?.created_by;
    }, [subtask]);

    const actionItems = useMemo(() => {
        if (canUser('editSubtask', [createdBy])) {
            if (canUser('removeSubtask', [createdBy])) {
                return UTIL_ACTION_ITEMS;
            }

            return NON_REMOVE_UTIL_ACTION_ITEMS;
        }

        return EMPTY_ARRAY;
    }, [createdBy]);

    const taskGroupsOptions = useMemo(() => {
        return taskGroups.map(taskGroup => ({ id: taskGroup.id, text: taskGroup.name }));
    }, [taskGroups]);

    const taskGroupsWithTasksOptions = useMemo(() => {
        return taskGroupsWithTasks.map(taskGroup => ({ id: taskGroup.id, text: taskGroup.name }))
    }, [taskGroupsWithTasks]);

    const selectedTaskGroupTasks = useMemo(() => {
        if (!moveToTaskSelectedTaskList) {
            return [];
        }

        return tasks
            .filter(task => task.task_group === moveToTaskSelectedTaskList)
            .map(task => ({ id: task.id, text: task.name }));
    }, [moveToTaskSelectedTaskList, tasks]);

    const handleUtilClick = useCallback(({ action }) => {
        switch (action) {
            case 'delete':
                onDelete && onDelete(id);
                break;

            case 'convertToTask':
                convertToTaskPopoutCtrl.current && convertToTaskPopoutCtrl.current.show();
                break;

            case 'moveToTask':
                moveToTaskPopoutCtrl.current && moveToTaskPopoutCtrl.current.show();
                break;

            default:
                return null;
        }
    }, [id, convertToTaskPopoutCtrl, moveToTaskPopoutCtrl, onDelete]);

    const handleConvertToTaskConfirmClick = useCallback(async () => {
        if (!convertToTaskSelectCtrl.current) {
            return;
        }

        convertToTaskPopoutCtrl.current && convertToTaskPopoutCtrl.current.hide();
        const taskGroupId = convertToTaskSelectCtrl.current.getValue();

        trackEvent('TASK.CREATE', {
            via: 'Covert from subtask'
        });

        await updateTask(id, {
            task_group: taskGroupId,
            task: null,
            order: Number.POSITIVE_INFINITY
        })

        if (parentTaskLabels.length) {
            return addTaskLabels(id, parentTaskLabels);
        }
    }, [id, parentTaskLabels, convertToTaskSelectCtrl, convertToTaskPopoutCtrl]);

    const handleConvertToTaskCancelClick = useCallback(() => {
        convertToTaskPopoutCtrl.current.hide();
    }, [convertToTaskPopoutCtrl]);

    const handleMoveToTaskConfirmClick = useCallback(() => {
        if (!moveToTaskTaskSelectCtrl.current) {
            return;
        }

        convertToTaskPopoutCtrl.current && convertToTaskPopoutCtrl.current.hide();

        updateTask(id, {
            task: moveToTaskTaskSelectCtrl.current.getValue()
        });
    }, [id, convertToTaskPopoutCtrl]);

    const handleMoveToTaskCancelClick = useCallback(() => {
        moveToTaskPopoutCtrl.current && moveToTaskPopoutCtrl.current.hide();
    }, [moveToTaskPopoutCtrl]);

    useEffect(() => {
        if (!moveToTaskSelectedTaskList && taskGroupsWithTasks.length) {
            setMoveToTaskSelectedTaskList(taskGroupsWithTasks[0].id)
        }
    }, [moveToTaskSelectedTaskList, taskGroupsWithTasks]);

    const handleMoveToTaskTaskListSelect = ({ id }) => {
        setMoveToTaskSelectedTaskList(id);
    }

    const handleDropdownShow = () => {
        setDropdownVisible(true);
    }

    const handleDropdownHide = () => {
        setDropdownVisible(false);
    }

    return (
        <React.Fragment>
            <DropdownBtn
                className={'item-actions' + (dropdownVisible ? ' visible' : '')}
                btnClassName={btnClassName}
                items={actionItems}
                onItemClick={handleUtilClick}
                onDropdownShow={handleDropdownShow}
                onDropdownHide={handleDropdownHide}
            >
                { icon }
            </DropdownBtn>

            <Popout
                provideController={convertToTaskPopoutCtrl}
                className="dark-arrow xxsmall"
                xAlign="right"
                defaultXPosition="left"
            >
                <div className="popout-head">
                    CONVERT SUBTASK TO TASK
                </div>

                <div className="popout-main">
                    <div className="popout-body">
                        <Select
                            label="Task List"
                            provideController={convertToTaskSelectCtrl}
                            options={taskGroupsOptions}
                        />
                    </div>
                </div>

                <div className="popout-foot">
                    <div className="btn-group">
                        <button className="btn btn-small btn-secondary" onClick={handleConvertToTaskCancelClick}>Cancel</button>

                        <button className="btn btn-small btn-success" onClick={handleConvertToTaskConfirmClick}>Convert</button>
                    </div>
                </div>
            </Popout>

            <Popout
                provideController={moveToTaskPopoutCtrl}
                className="dark-arrow xxsmall"
                xAlign="right"
                defaultXPosition="left"
            >
                <div className="popout-head">
                    MOVE SUBTASK INTO ANOTHER TASK
                </div>

                <div className="popout-main">
                    <div className="popout-body">
                        <Select
                            label="Task List"
                            provideController={moveToTaskTaskListSelectCtrl}
                            options={taskGroupsWithTasksOptions}
                            onSelect={handleMoveToTaskTaskListSelect}
                        />

                        <Select
                            label="Task"
                            provideController={moveToTaskTaskSelectCtrl}
                            options={selectedTaskGroupTasks}
                        />
                    </div>
                </div>

                <div className="popout-foot">
                    <div className="btn-group">
                        <button className="btn btn-small btn-secondary" onClick={handleMoveToTaskCancelClick}>Cancel</button>

                        <button className="btn btn-small btn-success" onClick={handleMoveToTaskConfirmClick}>Move</button>
                    </div>
                </div>
            </Popout>
        </React.Fragment>
    );
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getProjectTasks = makeGetProjectTasks();
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();
    const getSortedActiveProjectTaskGroupsWithTasks = makeGetSortedActiveProjectTaskGroupsWithTasks();

    return ({ taskGroups, tasks, notifications }, ownProps) => {
        const subtask = ownProps.id && getTask(tasks.items, ownProps.id);

        return {
            tasks: ownProps.projectId ? getProjectTasks(tasks.items, ownProps.projectId) : EMPTY_ARRAY,
            taskGroups: ownProps.projectId ? getSortedActiveProjectTaskGroups(taskGroups.items, ownProps.projectId) : EMPTY_ARRAY,
            taskGroupsWithTasks: ownProps.projectId ? getSortedActiveProjectTaskGroupsWithTasks(taskGroups.items, tasks.items, ownProps.projectId) : EMPTY_ARRAY,
            subtask,
            parentTaskLabels: getTask(tasks.items, ownProps.id)?.labels
        }
    };
};

export default connect(makeMapStateToProps)(SubtaskUtils);
